import { InviteTeamMemberModalProps } from './invite-team-member-modal.component';
import { useMutationInviteTeamMember } from '../../../team.queries';
import { KnownProjectTeamMemberDtoAccessLevel, SendInvitationDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { RestError } from '@azure/core-http';
import { useState } from 'react';
import _ from 'lodash';

type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

export type InviteTeamMemberReactFormState = {
  firstName?: string;
  lastName?: string;
  role?: string;
  accessLevel?: KnownProjectTeamMemberDtoAccessLevel | null;
  email?: string;
  mobile?: string;
  note?: string;
  externalID?: string;
};

interface InvitePreLoadedState {
  current: 'PRE_LOADED';
  saving: boolean;
  error?: RestError;
}

export type InviteTeamMemberActionStates = InvitePreLoadedState;

export interface InviteTeamMemberActionHandlers {
  onCancel(): void;
  onSubmit(form: InviteTeamMemberReactFormState): void;
}

export function useInviteTeamMemberAction({ ...props }: InviteTeamMemberModalProps) {
  const inviteTeamMemberMutation = useMutationInviteTeamMember();
  const [state, setState] = useState<InviteTeamMemberActionStates>({
    current: 'PRE_LOADED',
    saving: false
  });

  const handlers: InviteTeamMemberActionHandlers = {
    onCancel() {
      props.onAbort();
    },
    onSubmit(form) {
      setState((state) => ({ ...state, saving: true }));

      if (_.isEmpty(form)) {
        return;
      }

      const dto: DeepPartial<SendInvitationDto> = {
        account: {
          firstName: form.firstName,
          lastName: form.lastName,
          mobile: form.mobile || undefined,
          note: form.note || undefined,
          email: form.email || undefined,
          externalID: form.externalID || undefined,
          projects: [
            {
              projectID: props.projectID,
              accessLevel: form.accessLevel || undefined,
              role: form.role
            }
          ]
        }
      };

      if (!validateForm(dto)) {
        return;
      }
      inviteTeamMemberMutation.mutate(dto, {
        onSuccess() {
          props.onSuccess();
        },
        onError(error: any) {
          let restError: RestError;
          if (error instanceof RestError) {
            restError = error;
          } else if (error && 'message' in error && typeof error.message === 'string') {
            restError = new RestError(error.message);
          } else {
            restError = new RestError('There was a problem sending the invitation.');
          }
          setState((state) => ({ ...state, current: 'PRE_LOADED', saving: false, error: restError }));
        }
      });
    }
  };
  return [state, handlers] as const;
}

function validateForm(dto: DeepPartial<SendInvitationDto>): dto is SendInvitationDto {
  return !(
    _.isEmpty(dto.account?.firstName) ||
    _.isEmpty(dto.account?.lastName) ||
    // Either email or mobile is required
    (_.isEmpty(dto.account?.email) && _.isEmpty(dto.account?.mobile)) ||
    _.isEmpty(dto.account?.projects?.[0]?.accessLevel) ||
    _.isEmpty(dto.account?.projects?.[0]?.role)
  );
}
